/**
 * PodGallery — shared across admin, client, and driver portals.
 * Thumbnails load via the authenticated /api/files/{path} proxy.
 * Click any thumbnail to open a fullscreen lightbox with download + prev/next.
 */
import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { API } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";
import { Download, X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";

// ── Authenticated image loader ────────────────────────────────────────────────
function useFileSrc(path) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!path) return;
    let mounted = true;
    let objectUrl = null;
    (async () => {
      try {
        const token = localStorage.getItem("df_token");
        const r = await fetch(`${API}/files/${path}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const blob = await r.blob();
        objectUrl = URL.createObjectURL(blob);
        if (mounted) setSrc(objectUrl);
      } catch {}
    })();
    return () => {
      mounted = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);
  return src;
}

function triggerDownload(src, label) {
  if (!src) return;
  const a = document.createElement("a");
  a.href = src;
  a.download = `${(label || "file").replace(/\s+/g, "-")}.png`;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// ── Thumbnail ─────────────────────────────────────────────────────────────────
function Thumb({ path, label, index, onOpen, onSrcReady }) {
  const src = useFileSrc(path);

  useEffect(() => {
    if (src) onSrcReady(path, src);
  }, [src]); // eslint-disable-line

  return (
    <div className="relative group flex-shrink-0" style={{ width: 100, height: 100 }}>
      {src ? (
        <>
          <img
            src={src}
            alt={label}
            onClick={() => onOpen(index)}
            className="w-full h-full object-cover rounded-xl border border-white/10 cursor-zoom-in transition-transform duration-200 group-hover:scale-[1.05]"
          />
          {/* Hover overlay */}
          <div className="absolute inset-0 rounded-xl bg-black/55 opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex items-center justify-center gap-2 pointer-events-none group-hover:pointer-events-auto">
            <button
              onClick={(e) => { e.stopPropagation(); onOpen(index); }}
              className="p-1.5 rounded-lg bg-white/20 hover:bg-white/35 transition-colors"
              title="View full size"
            >
              <ZoomIn size={14} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); triggerDownload(src, label); }}
              className="p-1.5 rounded-lg bg-white/20 hover:bg-white/35 transition-colors"
              title="Download"
            >
              <Download size={14} />
            </button>
          </div>
          {label && (
            <span className="absolute bottom-1 left-1 text-[9px] bg-black/70 px-1.5 py-0.5 rounded pointer-events-none">
              {label}
            </span>
          )}
        </>
      ) : (
        <div className="w-full h-full bg-white/5 rounded-xl animate-pulse" />
      )}
    </div>
  );
}

// ── Lightbox ──────────────────────────────────────────────────────────────────
function Lightbox({ items, startIndex, srcMap, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  const item = items[idx];
  const src = srcMap[item?.path];

  const prev = useCallback(() => setIdx((i) => (i - 1 + items.length) % items.length), [items.length]);
  const next = useCallback(() => setIdx((i) => (i + 1) % items.length), [items.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft"  && items.length > 1) prev();
      if (e.key === "ArrowRight" && items.length > 1) next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, prev, next, items.length]);

  return createPortal(
    <div
      className="fixed inset-0 z-[9998] bg-black/93 backdrop-blur-sm flex flex-col items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>

        {/* Top bar */}
        <div className="flex items-center justify-between mb-4">
          <span className="text-white font-semibold text-sm">
            {item?.label}
            {items.length > 1 && (
              <span className="text-white/40 font-normal ml-2">
                {idx + 1} of {items.length}
              </span>
            )}
          </span>
          <div className="flex items-center gap-2">
            {src && (
              <button
                onClick={() => triggerDownload(src, item?.label)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold"
                style={{ background: "rgba(25,230,200,0.15)", color: "#19e6c8" }}
              >
                <Download size={13} /> Download
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Image + prev/next */}
        <div className="flex items-center gap-3">
          {items.length > 1 ? (
            <button
              onClick={prev}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex-shrink-0"
            >
              <ChevronLeft size={22} />
            </button>
          ) : <div style={{ width: 40 }} />}

          <div className="flex-1 flex items-center justify-center" style={{ minHeight: 260 }}>
            {src ? (
              <img
                src={src}
                alt={item?.label}
                className="max-h-[70vh] max-w-full rounded-2xl border border-white/10 object-contain shadow-2xl"
              />
            ) : (
              <div className="w-72 h-72 bg-white/5 rounded-2xl animate-pulse" />
            )}
          </div>

          {items.length > 1 ? (
            <button
              onClick={next}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex-shrink-0"
            >
              <ChevronRight size={22} />
            </button>
          ) : <div style={{ width: 40 }} />}
        </div>

        {/* Dot indicators */}
        {items.length > 1 && (
          <div className="flex justify-center gap-2 mt-5">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className="rounded-full transition-all duration-200"
                style={{
                  width:  i === idx ? 22 : 8,
                  height: 8,
                  background: i === idx ? "#19e6c8" : "rgba(255,255,255,0.2)",
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ── Exported gallery ──────────────────────────────────────────────────────────
export default function PodGallery({ imageUrls = [], pickupImageUrls = [], senderSignatureUrl, signatureUrl, podCompletedAt }) {
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [srcMap, setSrcMap] = useState({});

  const handleSrcReady = useCallback((path, src) => {
    setSrcMap((m) => ({ ...m, [path]: src }));
  }, []);

  const items = [
    ...pickupImageUrls.map((path, i) => ({ path, label: `Pickup Photo ${i + 1}` })),
    ...imageUrls.map((path, i) => ({ path, label: `POD Photo ${i + 1}` })),
    ...(senderSignatureUrl ? [{ path: senderSignatureUrl, label: "Sender Signature" }] : []),
    ...(signatureUrl ? [{ path: signatureUrl, label: "Receiver Signature" }] : []),
  ];

  if (items.length === 0) return null;

  return (
    <>
      <div className="flex flex-wrap gap-3">
        {items.map((item, i) => (
          <Thumb
            key={item.path}
            path={item.path}
            label={item.label}
            index={i}
            onOpen={setLightboxIdx}
            onSrcReady={handleSrcReady}
          />
        ))}
      </div>
      {podCompletedAt && (
        <p className="text-xs text-white/40 mt-2">
          Captured: {formatDateTime(podCompletedAt)}
        </p>
      )}
      {lightboxIdx !== null && (
        <Lightbox
          items={items}
          startIndex={lightboxIdx}
          srcMap={srcMap}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </>
  );
}
